/**
 * 需給ギャップ メインチャート (上段) の Y 軸 domain / ticks 算出。 きりのいい刻み
 * (AC-SDG-006、 1 / 2 / 5 × 10^n) の選び方は下段と共有する (`./niceAxis`)。
 *
 * **メイン軸は 0 対称にしない。** メインの 3 系列 (供給力合計 / 需要想定 /
 * 需要想定+販売) はいずれも非負で、 符号が付くのは差分チャートが描く position
 * (`ComputePosition` = 供給 − 需要) だけ。 メインを 0 対称にすると常に描画高の
 * 半分を空の負側に捨てることになるので、 対称レンジは AC-SDG-005 が明示している
 * 差分チャート (`./diffChartAxis`) にだけ残す。
 */
import { niceScale, stepTicks } from '~/pages/SupplyGap/models/niceAxis'

/**
 * 目盛り間隔の総数の上限 (= 目盛り線は最大 6 本)。
 * メインチャートは 2 枚組の上段 (flex 7) でプロット高を広く取れるため、 差分側の
 * 5 本より 1 本多く許して刻みを細かく保つ。
 */
const MAX_SEGMENTS = 5

/** 全系列 0 / データ無しのときの Y 軸上端 MW。 0 幅の軸は描けないため。 */
const EMPTY_MAX_MW = 1000

export type MainYAxis = {
  /** recharts `<YAxis domain>` (= [0, 上端])。 */
  domain: [number, number]
  /** recharts `<YAxis ticks>`。 0 から始まる昇順の目盛り値。 */
  ticks: number[]
}

/**
 * メインチャートの Y 軸を [0, 上端] の片側レンジで決める。
 * 刻み幅は {@link MAX_SEGMENTS} 本以内に収まる最小のきりのいい値を選ぶ
 * (= 余白が最小 = 系列が描画高を最も広く使う)。 刻み幅は必ず `max` の半分以下に
 * なるので、 上端は `max` の 1.5 倍未満に収まる。
 *
 * @param values メインチャートに描かれる値 (MW。 供給力合計 / 需要想定 / 需要想定+販売)。
 */
export function computeMainYAxis(values: readonly number[]): MainYAxis {
  let max = 0
  for (const v of values) {
    if (!Number.isFinite(v)) continue
    if (v > max) max = v
  }
  if (max === 0) max = EMPTY_MAX_MW

  const { step, segments } = niceScale(max, MAX_SEGMENTS)
  const ticks = stepTicks(step, 0, segments)
  return { domain: [0, ticks[ticks.length - 1]], ticks }
}
